import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { EmailCampaignFormInput } from '../EmailCampaignValidation';
import {
  PrepareConnectorFlowStepResponse,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { ActionButton } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/ActionButton';
import ErrorBoundary from '@/components/ErrorBoundary';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCompanyValues } from '@/providers';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowMappingsForm, FlowMappingsFormHandle } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { StepPageActions } from '@/global/components/Layout/StepPageActions/StepPageActions';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { FlowFormData, FlowMappingsConfig, FlowMappingsData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { EMAIL_CONNECTOR_FLOW_NAME } from '../constants';
import { useTranslation } from 'react-i18next';

type TemplateSelectionStepProps = {
  onBack: () => void;
  onNext: (formData: FlowFormData, mappingsData: FlowMappingsData) => Promise<boolean>;
};

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

export const TemplateSelectionStep: FC<TemplateSelectionStepProps> = ({ onBack, onNext }) => {
  const { id: workspaceId } = useWorkspace();
  const { setValue, watch } = useFormContext<EmailCampaignFormInput>();
  const selectedAudienceId = watch('audienceId');

  const { t } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');

  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FlowFormData>({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig>(null);
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [multiStepsCompleted, setMultiStepsCompleted] = useState(false);
  const mappingsFormRef = useRef<FlowMappingsFormHandle>(null);

  const { data: connectorFlows } = useGetConnectorFlowsQuery({
    workspaceId,
    queryParams: {
      showUserJourney: true,
    },
  });
  const {
    data: connectorInstances,
    isLoading: isLoadingInstances,
    refetch: refetchInstances,
  } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });

  const { currentLanguage } = useCompanyValues();

  const emailConnectorFlow = connectorFlows?.find((flow) => flow.name === EMAIL_CONNECTOR_FLOW_NAME);
  const emailConnectorFlowId = emailConnectorFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === emailConnectorFlow?.connectorId)?.id;

  const [prepareStep, { isLoading: isPreparing }] = usePrepareConnectorFlowStepMutation();

  useEffect(() => {
    setValue('connectorId', emailConnectorFlow?.connectorId || '');
    setValue('connectorFlowId', emailConnectorFlowId || '');
  }, [emailConnectorFlowId, emailConnectorFlow?.connectorId, setValue]);

  const fetchPrepareData = useCallback(async () => {
    if (!selectedAudienceId || !emailConnectorFlowId) return;
    if (!connectorInstanceId) {
      setPrepareError('No Email connector instance found. Please connect Email first and retry.');
      return;
    }

    setPrepareError(null);

    try {
      const response = await prepareStep({
        workspaceId,
        flowId: emailConnectorFlowId,
        audienceId: selectedAudienceId,
        stepId: 'step1',
        connectorInstanceId,
        stepData: {},
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);

      const prepareResultResponse = translatedResponse?.configuration?.elements?.params;

      setPrepareResult(prepareResultResponse as PrepareStepResult);
    } catch {
      setPrepareError(t('emailWorkspace.campaigns.failedToLoadTemplateOptions'));
      setPrepareResult(null);
    }
  }, [selectedAudienceId, emailConnectorFlowId, connectorInstanceId, prepareStep, workspaceId, currentLanguage, t]);

  useEffect(() => {
    setPrepareResult(null);
    setPrepareError(null);
    setFormData({});
    setMultiStepMappingsConfig(null);
    setMappingData({});
    setIsMappingsFormValid(false);
    setMultiStepsCompleted(false);
  }, [selectedAudienceId, emailConnectorFlowId, connectorInstanceId]);

  useEffect(() => {
    if (!emailConnectorFlowId || !selectedAudienceId) return;
    void fetchPrepareData();
  }, [fetchPrepareData, emailConnectorFlowId, selectedAudienceId, connectorInstanceId]);

  const multiStepFlowFormProps = {
    audienceId: selectedAudienceId!,
    connectorFlowId: emailConnectorFlowId!,
    connectorInstanceId: connectorInstanceId!,
    paramsConfig: prepareResult || {},
    onComplete: async (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
      setSubmitError(null);
      setFormData(allParams);
      setMultiStepMappingsConfig(mappingsConfig);
      setMappingData({});
      setIsMappingsFormValid(false);
      setMultiStepsCompleted(true);
      return;
    },
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const handleFinalSubmit = async () => {
    if (!isMappingsFormValid) {
      mappingsFormRef.current?.revealValidationErrors();
      return;
    }
    await onNext(formData, mappingData);
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: onBack,
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: handleFinalSubmit,
  };

  const pageTitle = t('emailWorkspace.campaigns.selectTemplatePageTitle');

  if (!emailConnectorFlowId) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-gray-600'>Initializing...</p>
      </div>
    );
  }

  if (isPreparing) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-gray-600'>Loading template options...</p>
      </div>
    );
  }

  if (!connectorInstanceId && !isLoadingInstances) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-red-600'>{t('whatsappWorkspace.noWhatsappConnector')}</p>
        <ActionButton handleBack={onBack} handleNext={async () => void (await refetchInstances())} isNextDisabled={false} isLoading={false} />
      </div>
    );
  }

  if (prepareError) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-red-600'>{prepareError}</p>
        <ActionButton handleBack={onBack} handleNext={fetchPrepareData} isNextDisabled={false} isLoading={false} />
      </div>
    );
  }

  const noFinalMappingAvailable = !multiStepMappingsConfig?.schema?.properties || Object.keys(multiStepMappingsConfig?.schema.properties).length === 0;

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-gray-600'>{t('emailWorkspace.campaigns.selectTemplatePageDescription')}</p>
      </div>
      {!multiStepsCompleted && (
        <ErrorBoundary>
          {prepareResult ? (
            <MultiStepFlowForm {...multiStepFlowFormProps} />
          ) : (
            <p className='text-sm text-gray-600'>{t('emailWorkspace.campaigns.noTemplatesFound')}</p>
          )}
        </ErrorBoundary>
      )}
      {submitError && <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{submitError}</div>}
      <ErrorBoundary>
        {(() => {
          if (!multiStepsCompleted || noFinalMappingAvailable || !multiStepMappingsConfig) {
            return null;
          }
          return (
            <div>
              <h3 className='mb-4 text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
              <FlowMappingsForm
                ref={mappingsFormRef}
                config={multiStepMappingsConfig}
                formData={mappingData}
                onFormDataChange={handleMappingDataChange}
                onValidationChange={handleMappingsValidationChange}
              />
              <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
            </div>
          );
        })()}
      </ErrorBoundary>
    </div>
  );
};
